import { PROJECT_MEDIA_TYPES } from 'consts'
import { SUB_SIDEBAR_KEYS } from 'consts/sidebar'
import { TabKey } from 'containers/SubSidebar/AIVideoContent/consts'
import { getIsMediaItemType } from 'containers/Timeline/MainContainer/Tracks/utils'
import { aiImageSelectors } from 'store/features/aiImage/selectors'
import { aiVideoSelectors } from 'store/features/aiVideo/selectors'
import { aiAvatarSelectors } from 'store/features/aiAvatar/selectors'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { projectSelectors } from 'store/features/project/selectors'
import { isSmartEditModeSelector } from 'store/selectors'
import { withAppThunks } from 'store/with-app-thunks'
import { TAppDispatch, TGetState } from 'store/types'
import {
  TActiveTimelineItemType,
  IItemClickHandlerParams
} from 'store/types/editor'
import { TAudioType } from 'types/audio'
import { TMediaItemType, IMediaItem, MediaStatus } from 'types/media'
import { getDeviceVersion, scrollToElementById } from 'utils'
import { isAudioItem } from 'utils/audio'
import { PROJECT_AUDIO_TYPES } from 'consts/audio'
import { OverlayItemType } from 'types'
import editorSlice from 'store/features/editor/slice'

import { editorSelectors } from 'store/features/editor/selectors'
const { actions } = editorSlice

const isMediaItemGenerating = (
  state: ReturnType<TGetState>,
  id: string
): boolean => {
  const mediaItems = projectDataSelectors.mediaItems(state)
  const item = mediaItems.find(m => m.id === id)
  return item?.meta?.status === MediaStatus.PENDING
}

const selectTimelineItem =
  (id: string, type: TMediaItemType | TAudioType) =>
  (dispatch: TAppDispatch, getState: TGetState) => {
    const isMobile = getDeviceVersion()
    const state = getState()

    const activeTimelineItem = editorSelectors.activeTimelineItem(state)
    const activeSidebarTab = editorSelectors.activeSidebarTab(state)
    const isTtsAudioItem = type === PROJECT_AUDIO_TYPES.TTS
    const isTextItem = type === PROJECT_MEDIA_TYPES.text
    const isOverlayClick =
      type === PROJECT_MEDIA_TYPES.aiVideo ||
      type === PROJECT_MEDIA_TYPES.aiImage ||
      type === PROJECT_MEDIA_TYPES.image ||
      isTextItem
    const isWithoutMobileOverlay = isMobile

    const isAiVideoClick = type === PROJECT_MEDIA_TYPES.aiVideo

    if (activeTimelineItem.id !== id || activeTimelineItem.type !== type) {
      dispatch(actions.setActiveTimelineItems([{ id, type }]))
    }
    if (isTtsAudioItem && activeSidebarTab !== SUB_SIDEBAR_KEYS.TTS) {
      dispatch(actions.setActiveSidebarTab(SUB_SIDEBAR_KEYS.TTS))
    }

    if (isAiVideoClick) {
      if (isMobile) {
        return
      }
    } else if (isOverlayClick) {
      if (isWithoutMobileOverlay) {
        return
      }
      if (isMediaItemGenerating(state, id)) {
        return
      }

      dispatch(actions.setAsideOverlayContent(type as OverlayItemType))
      dispatch(actions.setIsMobileSubSidebarOpen(true))
    } else {
      dispatch(actions.setAsideOverlayContent(null))
    }
  }

// Populates the regeneration form for an aiVideo timeline item and opens the
// smart-edit overlay when the item qualifies (e.g. gen-ai scenes generated in
// this project).
const openAiVideoRegeneration = (hisotoryItemId?: number | string) =>
  withAppThunks(
    appThunks => async (dispatch: TAppDispatch, getState: TGetState) => {
      const timelineItemId = editorSelectors.activeTimelineItemId(getState())
      if (!timelineItemId) {
        return
      }
      dispatch(actions.setActiveSidebarTab(SUB_SIDEBAR_KEYS.AI_VIDEO))
      await dispatch(
        appThunks.aiVideoThunks.setRegenerationForm(
          timelineItemId,
          hisotoryItemId
        )
      )
      // Regenerating from the version history adds the result as a new timeline
      // clip instead of replacing the active scene. The form still targets the
      // scene (so smart-edit stays available as the generation source) — the
      // generation handlers branch on this flag to add rather than replace.
      dispatch(
        appThunks.aiVideoThunks.updateGenerationParams({ addAsNewItem: true })
      )
      dispatch(appThunks.aiVideoThunks.setActiveTab(TabKey.GENERATE))

      const isSmartEdit = isSmartEditModeSelector(getState())

      if (isSmartEdit) {
        dispatch(actions.setAsideOverlayContent(OverlayItemType.AI_VIDEO))
      } else {
        dispatch(actions.setAsideOverlayContent(null))
      }
    }
  )

const onMediaItemSelection = (timelineItemId: string, type: TMediaItemType) =>
  withAppThunks(
    appThunks => async (dispatch: TAppDispatch, getState: TGetState) => {
      const isGenAiProject = !!projectSelectors.meta(getState())?.genAiProjectId
      const isGenerating = isMediaItemGenerating(getState(), timelineItemId)
      const isMobile = getDeviceVersion()

      if (isGenerating) {
        const formResetHandlers: Partial<Record<string, () => void>> = {
          [PROJECT_MEDIA_TYPES.aiVideo]: () => {
            const currentTargetId =
              aiVideoSelectors.targetMediaItemId(getState())
            if (currentTargetId !== timelineItemId) {
              dispatch(appThunks.aiVideoThunks.clearGenerationParams())
            }
            dispatch(appThunks.aiVideoThunks.setActiveTab(TabKey.GENERATE))
            dispatch(actions.setAsideOverlayContent(null))
            dispatch(actions.setActiveSidebarTab(SUB_SIDEBAR_KEYS.AI_VIDEO))
          },
          [PROJECT_MEDIA_TYPES.aiImage]: () => {
            const currentTargetId =
              aiImageSelectors.targetMediaItemId(getState())
            if (currentTargetId && currentTargetId !== timelineItemId) {
              dispatch(appThunks.aiImageThunks.resetGenerateForm())
            }
          },
          [PROJECT_MEDIA_TYPES.aiAvatar]: () => {
            dispatch(actions.setActiveSidebarTab(SUB_SIDEBAR_KEYS.AI_AVATAR))
          }
        }
        formResetHandlers[type]?.()
        return
      }

      const handleImageSelection = () => {
        dispatch(appThunks.aiImageThunks.setRegenerationForm(timelineItemId))
      }

      const handleAIAvatarSelection = async () => {
        await dispatch(
          appThunks.aiAvatarThunks.setRegenerationForm(timelineItemId)
        )
        dispatch(actions.setActiveSidebarTab(SUB_SIDEBAR_KEYS.AI_AVATAR))
      }

      const mediaTypeHandlers: Partial<
        Record<string, () => void | Promise<void>>
      > = {
        [PROJECT_MEDIA_TYPES.aiVideo]: async () => {
          await dispatch(
            appThunks.aiVideoThunks.setRegenerationForm(timelineItemId)
          )
          dispatch(appThunks.aiVideoThunks.setActiveTab(TabKey.GENERATE))

          const isSmartEdit = isSmartEditModeSelector(getState())

          if (isSmartEdit) {
            dispatch(actions.setAsideOverlayContent(OverlayItemType.AI_VIDEO))
          } else {
            dispatch(actions.setAsideOverlayContent(null))
            dispatch(actions.setActiveSidebarTab(SUB_SIDEBAR_KEYS.AI_VIDEO))
          }
        },
        [PROJECT_MEDIA_TYPES.aiImage]: handleImageSelection,
        [PROJECT_MEDIA_TYPES.aiAvatar]: handleAIAvatarSelection,
        [PROJECT_MEDIA_TYPES.image]: handleImageSelection,
        [PROJECT_MEDIA_TYPES.text]: () => {
          if (!isMobile) {
            dispatch(appThunks.editorThunks.setToDefaultTab())
          }
        }
      }

      const isImageType =
        type === PROJECT_MEDIA_TYPES.aiImage ||
        type === PROJECT_MEDIA_TYPES.image

      const isAiVideoType = type === PROJECT_MEDIA_TYPES.aiVideo
      const isAiAvatarType = type === PROJECT_MEDIA_TYPES.aiAvatar

      const sidebarTab =
        isImageType && !isGenAiProject
          ? SUB_SIDEBAR_KEYS.AI_IMAGE
          : SUB_SIDEBAR_KEYS.AI_VIDEO

      if (!(isAiVideoType && isGenAiProject)) {
        if (type !== PROJECT_MEDIA_TYPES.text && !isAiAvatarType) {
          dispatch(actions.setActiveSidebarTab(sidebarTab))
        }
      }

      const handler = mediaTypeHandlers[type]

      if (handler) {
        await handler()
      } else {
        dispatch(appThunks.editorThunks.setToDefaultTab())
      }
    }
  )

const onAudioItemSelection = (type: TAudioType) =>
  withAppThunks(appThunks => (dispatch: TAppDispatch) => {
    const audioTypeHandlers = {
      [PROJECT_AUDIO_TYPES.SOUND]: () => {
        dispatch(
          actions.setAsideOverlayContent(OverlayItemType.MUSIC_VERSIONING)
        )
      },
      [PROJECT_AUDIO_TYPES.VOICE_OVER]: () => {
        dispatch(
          actions.setAsideOverlayContent(OverlayItemType.MUSIC_VERSIONING)
        )
      },
      [PROJECT_AUDIO_TYPES.TTS]: () => {
        dispatch(actions.setActiveSidebarTab(SUB_SIDEBAR_KEYS.TTS))
        dispatch(actions.setAsideOverlayContent(null))
      }
    }

    const handler = audioTypeHandlers[type]

    if (handler) {
      handler()
    } else {
      dispatch(appThunks.editorThunks.setToDefaultTab())
    }
  })

const openItemTypeSpecificTab =
  (timelineItemId: string, type: TActiveTimelineItemType) =>
  (dispatch: TAppDispatch) => {
    if (getIsMediaItemType(type)) {
      dispatch(onMediaItemSelection(timelineItemId, type as TMediaItemType))
    } else if (isAudioItem(type)) {
      dispatch(onAudioItemSelection(type as TAudioType))
    }
  }

const selectSingleTimelineItem =
  (params: IItemClickHandlerParams) => (dispatch: TAppDispatch) => {
    dispatch(selectTimelineItem(params.id, params.type))
    dispatch(openItemTypeSpecificTab(params.id, params.type))
    scrollToElementById(params.id)
  }

const deselectAllTimelineItems = () =>
  withAppThunks(appThunks => (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const formCleaningMap: Record<string, () => void> = {
      [SUB_SIDEBAR_KEYS.AI_IMAGE]: () =>
        dispatch(appThunks.aiImageThunks.resetGenerateForm()),
      [SUB_SIDEBAR_KEYS.AI_VIDEO]: () =>
        dispatch(appThunks.aiVideoThunks.clearGenerationParams()),
      [SUB_SIDEBAR_KEYS.AI_AVATAR]: () =>
        dispatch(appThunks.aiAvatarThunks.resetGenerationParams())
    }
    const aiSidebarTabs = Object.keys(formCleaningMap)
    const activeTimelineItems = editorSelectors.activeTimelineItems(state)
    if (!activeTimelineItems.length) {
      return
    }
    const currentSidebarTab = editorSelectors.activeSidebarTab(state)
    const activeOverlay = editorSelectors.activeAsideOverlayKey(state)
    const isAiVideoOverlay = activeOverlay === OverlayItemType.AI_VIDEO

    if (isAiVideoOverlay) {
      dispatch(appThunks.aiVideoThunks.clearGenerationParams())
    } else if (currentSidebarTab && aiSidebarTabs.includes(currentSidebarTab)) {
      const formByTab: Record<string, { targetMediaItemId: string | null }> = {
        [SUB_SIDEBAR_KEYS.AI_IMAGE]: aiImageSelectors.generateForm(state),
        [SUB_SIDEBAR_KEYS.AI_VIDEO]: aiVideoSelectors.generationParams(state),
        [SUB_SIDEBAR_KEYS.AI_AVATAR]: aiAvatarSelectors.generationParams(state)
      }

      // Only reset when the active form is a regeneration form
      // (targetMediaItemId points at the clip being edited).
      const isRegenerationForm =
        !!formByTab[currentSidebarTab]?.targetMediaItemId
      if (isRegenerationForm) {
        aiSidebarTabs.forEach(tab => formCleaningMap[tab]())
      }
    }

    // Clear the avatar regen form whenever the clip it targets is the one being
    // deselected — independent of the active sidebar tab. The tab-keyed branch
    // above misses the case where the user selected an avatar clip, switched to
    // another tab (the clip stays selected), then deselected it: the avatar tab
    // would otherwise reopen with the deselected clip's stale data. A fresh form
    // (targetMediaItemId === null) never matches, so in-progress input is kept.
    const avatarTargetId =
      aiAvatarSelectors.generationParams(state).targetMediaItemId
    const isDeselectingAvatarTarget = activeTimelineItems.some(
      item =>
        item.type === PROJECT_MEDIA_TYPES.aiAvatar && item.id === avatarTargetId
    )
    if (isDeselectingAvatarTarget) {
      dispatch(appThunks.aiAvatarThunks.resetGenerationParams())
    }

    dispatch(actions.setActiveTimelineItems([]))
    dispatch(actions.setAsideOverlayContent(null))
  })

// if the item is still active, reselect it to bring recent data
// mainly used for ai video and ai image generation after generation is completed
const reselectActiveTimelineMediaItem =
  (targetItem?: IMediaItem) =>
  (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const activeTimelineItem = editorSelectors.activeTimelineItem(state)
    const activeSidebarTab = editorSelectors.activeSidebarTab(state)

    if (
      targetItem &&
      targetItem.id === activeTimelineItem.id &&
      (activeSidebarTab === SUB_SIDEBAR_KEYS.AI_VIDEO ||
        activeSidebarTab === SUB_SIDEBAR_KEYS.AI_IMAGE)
    ) {
      dispatch(
        selectSingleTimelineItem({
          id: targetItem.id,
          type: targetItem.type
        })
      )
    }
  }

export const selectionThunks = {
  selectTimelineItem,
  openItemTypeSpecificTab,
  selectSingleTimelineItem,
  deselectAllTimelineItems,
  reselectActiveTimelineMediaItem,
  openAiVideoRegeneration
}
